// Chat with Claude: a list of conversations (and what Claude remembers), and a conversation view
// that streams replies from POST /api/chat (server-sent events).
import { api, ApiError, supabase } from "./api";
import { dateTime, esc } from "./format";
import { markdown } from "./markdown";

interface Summary { id: string; title: string; updatedAt: string }
interface Note { id: number; text: string }
interface Display { role: "user" | "assistant"; text: string; tools: string[]; sources: { url: string; title: string }[]; cost?: number }
interface ChatList { enabled: boolean; conversations: Summary[]; notes: Note[] }

const SUGGESTIONS = [
  "What's moving on my watchlist, and why?",
  "How healthy is BHP right now?",
  "Compare CBA, WBC, NAB and ANZ on valuation and dividends",
  "Which of my watchlist stocks report results soon?",
];

let list: ChatList | null = null;
let streaming = false;

const wide = () => matchMedia("(min-width: 900px)").matches;

function listHtml(activeId: string | null) {
  if (!list) return `<div class="loading" role="status"><span></span>Loading…</div>`;
  return `
    <a class="primary new-chat" href="#/chat/new">New chat</a>
    <ul class="convs">${list.conversations.map((c) => `
      <li class="${c.id === activeId ? "on" : ""}"><a href="#/chat/${c.id}"><span>${esc(c.title)}</span><small class="muted">${dateTime(c.updatedAt)}</small></a></li>`).join("") ||
      `<li class="muted small empty">No conversations yet.</li>`}</ul>
    <details class="memory"${list.notes.length ? "" : " hidden"}>
      <summary>What Claude remembers (${list.notes.length})</summary>
      <ul>${list.notes.map((n) => `<li><span>${esc(n.text)}</span><button class="ghost small" data-forget="${n.id}" aria-label="Forget this note">Forget</button></li>`).join("")}</ul>
    </details>`;
}

function assistantInner(m: Display) {
  const tools = m.tools.length ? `<ul class="tools">${m.tools.map((t) => `<li>${esc(t)}</li>`).join("")}</ul>` : "";
  const sources = m.sources.length
    ? `<details class="sources-list"><summary>${m.sources.length} source${m.sources.length > 1 ? "s" : ""}</summary><ol>${m.sources.map((s) => `<li><a href="${esc(s.url)}" target="_blank" rel="noopener">${esc(s.title)}</a></li>`).join("")}</ol></details>`
    : "";
  const cost = m.cost != null ? `<span class="cost muted">about US$${m.cost < 0.01 ? "0.01" : m.cost.toFixed(2)}</span>` : "";
  return `${tools}<div class="body md">${markdown(m.text)}</div>${sources}${cost}`;
}

const bubble = (m: Display) =>
  m.role === "user"
    ? `<div class="msg user"><div class="body">${esc(m.text).replace(/\n/g, "<br>")}</div></div>`
    : `<div class="msg assistant">${assistantInner(m)}</div>`;

const TYPING = `<div class="typing" aria-label="Claude is replying"><span></span><span></span><span></span></div>`;

async function refreshList() {
  list = await api<ChatList>("chat");
}

export async function renderChat(app: HTMLElement, id: string | null, draft = "") {
  const showList = !id || wide();
  const showMain = !!id;
  app.innerHTML = `
    <section class="chat ${showMain ? "has-main" : ""}">
      ${showList ? `<aside class="chat-side" id="chat-side">${listHtml(id)}</aside>` : ""}
      ${showMain ? `<div class="chat-main">
        <div class="chat-top"><a href="#/chat" class="back">‹ Chats</a><h2 id="chat-title">${id === "new" ? "New chat" : ""}</h2>
          ${id !== "new" ? `<button class="ghost small" id="chat-delete">Delete</button>` : ""}</div>
        <div class="msgs" id="msgs" aria-live="polite"></div>
        <form class="composer" id="composer">
          <textarea name="message" rows="1" placeholder="Message Claude" aria-label="Message" maxlength="8000"></textarea>
          <button type="submit" class="primary" aria-label="Send">Send</button>
        </form>
      </div>` : ""}
    </section>`;

  const showDisabled = () => {
    const msgs = document.getElementById("msgs");
    if (!msgs || msgs.querySelector(".notice")) return;
    msgs.insertAdjacentHTML("afterbegin", `<div class="notice warn">Chat isn't switched on yet: the site needs an Anthropic API key. Ask the websites admin to add the secret <code>ANTHROPIC_API_KEY</code> to this site's Worker.</div>`);
  };
  const side = document.getElementById("chat-side");
  const wireSide = () => {
    side?.querySelectorAll<HTMLButtonElement>("[data-forget]").forEach((b) => b.addEventListener("click", async () => {
      b.disabled = true;
      list = { ...list!, ...(await api<ChatList>(`notes/${b.dataset.forget}`, "DELETE")) };
      side.innerHTML = listHtml(id);
      wireSide();
    }));
  };
  if (side) {
    refreshList().then(() => { side.innerHTML = listHtml(id); wireSide(); if (showMain && !list!.enabled) showDisabled(); })
      .catch((e) => (side.innerHTML = `<div class="notice error">${esc(e.message)}</div>`));
  } else {
    refreshList().then(() => list!.enabled || showDisabled()).catch(() => {});
  }
  if (!showMain) return;

  const msgs = document.getElementById("msgs")!;
  const form = document.getElementById("composer") as HTMLFormElement;
  const box = form.elements.namedItem("message") as HTMLTextAreaElement;
  let conversationId = id === "new" ? null : id;

  const atBottom = () => msgs.scrollHeight - msgs.scrollTop - msgs.clientHeight < 80;
  const toBottom = () => (msgs.scrollTop = msgs.scrollHeight);
  const grow = () => { box.style.height = "auto"; box.style.height = Math.min(box.scrollHeight, 180) + "px"; };
  box.addEventListener("input", grow);
  box.addEventListener("keydown", (e) => {
    // Enter sends on a keyboard; on touch screens Enter is a new line and the button sends.
    if (e.key === "Enter" && !e.shiftKey && !matchMedia("(pointer: coarse)").matches) { e.preventDefault(); form.requestSubmit(); }
  });

  if (conversationId) {
    msgs.innerHTML = `<div class="loading" role="status"><span></span>Loading…</div>`;
    try {
      const c = await api<{ title: string; messages: Display[] }>(`chat/${conversationId}`);
      document.getElementById("chat-title")!.textContent = c.title;
      msgs.innerHTML = c.messages.map(bubble).join("");
      toBottom();
    } catch (e) {
      msgs.innerHTML = `<div class="notice error">${esc((e as Error).message)}</div>`;
      return;
    }
    document.getElementById("chat-delete")?.addEventListener("click", async () => {
      if (!confirm("Delete this conversation?")) return;
      await api(`chat/${conversationId}`, "DELETE");
      location.hash = "#/chat";
    });
  } else {
    msgs.innerHTML = `<div class="suggest"><p class="muted">Claude can look up any ASX stock's prices, financials, dividends and announcements, search the web for news, manage your watchlist, and remember notes about you.</p>
      ${SUGGESTIONS.map((s) => `<button type="button" class="chip">${esc(s)}</button>`).join("")}</div>`;
    msgs.querySelectorAll<HTMLButtonElement>(".chip").forEach((b) => b.addEventListener("click", () => { box.value = b.textContent ?? ""; grow(); box.focus(); }));
  }
  if (draft) { box.value = draft; grow(); }
  if (!wide() || draft) box.focus();

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const message = box.value.trim();
    if (!message || streaming) return;
    streaming = true;
    form.classList.add("busy");
    msgs.querySelector(".suggest")?.remove();
    msgs.insertAdjacentHTML("beforeend", bubble({ role: "user", text: message, tools: [], sources: [] }));
    box.value = "";
    grow();
    const reply: Display = { role: "assistant", text: "", tools: [], sources: [] };
    msgs.insertAdjacentHTML("beforeend", `<div class="msg assistant pending">${TYPING}</div>`);
    const el = msgs.lastElementChild as HTMLElement;
    toBottom();
    let frame = 0;
    const paint = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const stick = atBottom();
        el.innerHTML = assistantInner(reply) + (streaming ? TYPING : "");
        if (stick) toBottom();
      });
    };
    try {
      await stream({ conversationId, message }, (ev) => {
        if (ev.type === "start") conversationId = ev.conversationId;
        else if (ev.type === "text") reply.text += ev.text;
        else if (ev.type === "tool") reply.tools.push(ev.label);
        else if (ev.type === "source" && !reply.sources.some((s) => s.url === ev.url)) reply.sources.push({ url: ev.url, title: ev.title });
        else if (ev.type === "done") {
          reply.cost = ev.cost;
          if (id === "new") {
            history.replaceState(null, "", `#/chat/${ev.conversationId}`);
            document.getElementById("chat-title")!.textContent = ev.title;
            id = ev.conversationId;
          }
        } else if (ev.type === "error") throw new Error(ev.message);
        paint();
      });
    } catch (err) {
      reply.text += `${reply.text ? "\n\n" : ""}**Something went wrong:** ${(err as Error).message}`;
      if (err instanceof ApiError && err.status === 503) showDisabled();
      if (!box.value) { box.value = message; grow(); } // so it can be sent again
    } finally {
      streaming = false;
      form.classList.remove("busy");
      el.classList.remove("pending");
      paint();
      if (side) refreshList().then(() => { side.innerHTML = listHtml(id); wireSide(); });
    }
  });
}

type ChatEvent =
  | { type: "start"; conversationId: string }
  | { type: "text"; text: string }
  | { type: "tool"; label: string }
  | { type: "source"; url: string; title: string }
  | { type: "done"; conversationId: string; title: string; cost: number }
  | { type: "error"; message: string };

async function stream(body: { conversationId: string | null; message: string }, on: (ev: ChatEvent) => void) {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new ApiError("Please sign in.", 401);
  const r = await fetch("/api/chat", {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!r.ok || !r.body) {
    const err = await r.json().catch(() => ({}));
    throw new ApiError(err.error ?? `Request failed (${r.status})`, r.status);
  }
  const reader = r.body.pipeThrough(new TextDecoderStream()).getReader();
  let buf = "";
  let finished = false;
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += value;
    let cut;
    while ((cut = buf.indexOf("\n\n")) >= 0) {
      const chunk = buf.slice(0, cut);
      buf = buf.slice(cut + 2);
      const line = chunk.split("\n").find((l) => l.startsWith("data: "));
      if (!line) continue;
      const ev = JSON.parse(line.slice(6)) as ChatEvent;
      if (ev.type === "done") finished = true;
      on(ev);
    }
  }
  if (!finished) throw new Error("The connection closed before Claude finished. Your message may not have been saved.");
}
